if (!customElements.get('shoppable-layout-slider')) {
  class ShoppableLayoutSlider extends HTMLElement {
    constructor() {
      super();
      this.style.opacity = '0'; // hidden until initialized
      this.style.transition = 'opacity 0.3s ease-out';

      if (Shopify.designMode) {
        this._onSectionLoad = this.observeSection.bind(this);
        window.addEventListener('shopify:section:load', this._onSectionLoad);
      }
      this.observeSection();
    }

    disconnectedCallback() {
      if (this._onSectionLoad) {
        window.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (this.videoObserver) this.videoObserver.disconnect();
      if (this.swiper && this.swiper.destroy) {
        this.swiper.destroy(true, false);
        this.swiper = null;
      }
    }

    observeSection() {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
              observer.disconnect(); // remove observer after initialized
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(this);
    }

    init() {
      if (this.swiper) return; // prevent reinitialization

      const swiperContainer = this.querySelector('.swiper-shoppable-slider');
      if (!swiperContainer) return;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.swiper = new Swiper(swiperContainer, {
        autoplay: false,
        // mobile: one full slide centered with a peek of the previous/next;
        // centeredSlidesBounds keeps the FIRST slide left-aligned (no empty
        // gutter) and the LAST right-aligned, centering only the middle ones
        spaceBetween: 12,
        slidesPerView: 1.3,
        centeredSlides: true,
        centeredSlidesBounds: true,
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        breakpoints: {
          1200: {
            spaceBetween: 40,
            slidesPerView: 3.8,
            centeredSlides: false
          },
          990: {
            spaceBetween: 32,
            slidesPerView: 3.4,
            centeredSlides: false
          },
          750: {
            spaceBetween: 24,
            slidesPerView: 2.4,
            centeredSlides: false
          }
        },
        on: {
          init: () => {
            this.style.opacity = '1'; // show after initialized
            this.setupVideos();
            this.setupMuteToggles();
          }
        }
      });
    }

    /* Autoplay (muted) only when the video is on screen and the user has not
       asked for reduced motion; otherwise the poster image is shown. */
    setupVideos() {
      const videos = this.querySelectorAll('video');
      if (this.reducedMotion) {
        videos.forEach(video => {
          video.autoplay = false;
          video.pause();
        });
        return;
      }
      this.videoObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.playsInline = true;
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.35 }
      );
      videos.forEach(video => {
        video.muted = true; // necessary for autoplay
        video.playsInline = true;
        this.videoObserver.observe(video);
      });
    }

    /* Circular mute/unmute toggle on each video tile.
       Unmuting one video mutes every other shoppable video on the page. */
    setupMuteToggles() {
      this.querySelectorAll('[data-shoppable-mute]').forEach(btn => {
        if (btn.dataset.muteBound) return;
        btn.dataset.muteBound = 'true';
        btn.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          const video = btn
            .closest('.swiper-slide__inner')
            ?.querySelector('video');
          if (!video) return;
          const unmuting = video.muted;
          if (unmuting) {
            // mute all other shoppable videos first (page-wide)
            document
              .querySelectorAll('shoppable-layout-slider video')
              .forEach(v => {
                if (v !== video) v.muted = true;
              });
            document
              .querySelectorAll('[data-shoppable-mute]')
              .forEach(b => ShoppableLayoutSlider.syncMuteBtn(b, true));
            video.muted = false;
            // explicit user gesture — play even under prefers-reduced-motion
            if (video.paused) {
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            }
          } else {
            video.muted = true;
          }
          ShoppableLayoutSlider.syncMuteBtn(btn, video.muted);
        });
      });
    }

    static syncMuteBtn(btn, muted) {
      btn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      btn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    open(event, index, id) {
      document.querySelector(`shoppable-drawer.shoppable-drawer--${id}`).openWithIndex(event, index);
    }
  }

  customElements.define('shoppable-layout-slider', ShoppableLayoutSlider);
}
